import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  Inbox,
  LifeBuoy,
  Loader2,
  LockKeyhole,
  MessageSquareText,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import {
  getAdminSupportTicket,
  listAdminSupportTickets,
  replyAdminSupportTicket,
  updateAdminSupportTicket,
} from "@/lib/api";
import { fmtDateTime, timeAgo } from "@/lib/formatters";

const STATUSES = [
  ["new", "New"],
  ["open", "Open"],
  ["waiting_on_support", "Waiting on support"],
  ["waiting_on_customer", "Waiting on customer"],
  ["resolved", "Resolved"],
  ["closed", "Closed"],
];

const PRIORITIES = [
  ["low", "Low"],
  ["normal", "Normal"],
  ["high", "High"],
  ["urgent", "Urgent"],
];

const QUEUES = [
  { id: "all", label: "All active", icon: Inbox },
  { id: "unassigned", label: "Unassigned", icon: Users },
  { id: "mine", label: "My queue", icon: UserCheck },
  { id: "breached", label: "SLA breached", icon: AlertOctagon },
];

function titleCase(value = "") {
  return value.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function statusTone(status) {
  if (status === "waiting_on_customer") return "bg-amber-50 text-amber-800 border-amber-200";
  if (status === "resolved" || status === "closed") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (status === "new") return "bg-sky-50 text-sky-800 border-sky-200";
  return "bg-indigo-50 text-indigo-800 border-indigo-200";
}

function priorityTone(priority) {
  if (priority === "urgent") return "bg-rose-50 text-rose-800 border-rose-200";
  if (priority === "high") return "bg-orange-50 text-orange-800 border-orange-200";
  if (priority === "low") return "bg-slate-50 text-slate-600 border-slate-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
}

function sla(ticket) {
  const due = new Date(ticket.firstResponseDueAt).getTime();
  const responded = Boolean(ticket.firstRespondedAt);
  const diff = due - Date.now();
  if (responded) return { label: "First response met", tone: "text-emerald-700 bg-emerald-50" };
  if (diff <= 0) return { label: `${Math.max(1, Math.floor(Math.abs(diff) / 3600000))}h breached`, tone: "text-rose-700 bg-rose-50" };
  if (diff <= 3600000) return { label: `${Math.max(1, Math.ceil(diff / 60000))}m remaining`, tone: "text-amber-700 bg-amber-50" };
  return { label: `${Math.ceil(diff / 3600000)}h remaining`, tone: "text-slate-600 bg-slate-50" };
}

export default function SupportTickets() {
  const { currentAdmin } = useApp();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [queue, setQueue] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const debouncedSearch = search.trim();

  const filters = useMemo(() => ({
    assigneeId: queue === "unassigned" ? "unassigned" : queue === "mine" ? currentAdmin?.id : "",
    limit: 70,
    priority,
    query: debouncedSearch,
    status,
  }), [currentAdmin?.id, debouncedSearch, priority, queue, status]);

  const listQuery = useQuery({
    queryKey: ["admin-support", filters],
    queryFn: ({ signal }) => listAdminSupportTickets(filters, signal),
    refetchInterval: 30_000,
  });
  const detailQuery = useQuery({
    enabled: Boolean(selectedId),
    queryKey: ["admin-support-detail", selectedId],
    queryFn: ({ signal }) => getAdminSupportTicket(selectedId, signal),
    refetchInterval: 20_000,
  });

  const items = useMemo(() => {
    const all = listQuery.data?.items || [];
    if (queue !== "breached") return all;
    return all.filter((ticket) => !ticket.firstRespondedAt && new Date(ticket.firstResponseDueAt).getTime() < Date.now());
  }, [listQuery.data?.items, queue]);

  useEffect(() => {
    if (!selectedId && items.length) setSelectedId(items[0].id);
    if (selectedId && items.length && !items.some((ticket) => ticket.id === selectedId)) setSelectedId(items[0].id);
  }, [items, selectedId]);

  const selected = detailQuery.data?.ticket;
  const metrics = listQuery.data?.metrics || {};

  return (
    <div data-testid="support-page">
      <PageHeader
        eyebrow="Support operations"
        title="Case Room"
        subtitle="A focused triage workspace for merchant conversations, ownership and SLA control."
      />

      <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-5">
        <KpiCard label="Active workload" value={metrics.open || 0} icon={LifeBuoy} accent="#BAE6FD" hint="All unresolved cases" />
        <KpiCard label="Unassigned" value={metrics.unassigned || 0} icon={Users} accent="#E0E7FF" hint="Needs an owner" />
        <KpiCard label="SLA breached" value={metrics.breached || 0} icon={Clock3} accent="#FEE2E2" hint="First response" />
        <KpiCard label="Urgent" value={metrics.urgent || 0} icon={AlertOctagon} accent="#FFEDD5" />
        <div className="col-span-2 rounded-xl border border-[#0E5B52]/10 bg-[linear-gradient(135deg,#0E5B52,#08766a)] p-4 text-white shadow-sm xl:col-span-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/65"><Sparkles className="h-3.5 w-3.5 text-amber-300" /> Live pulse</div>
          <div className="mt-3 text-2xl font-semibold">{metrics.escalated || 0}</div>
          <div className="text-xs text-white/70">escalated cases</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[#0E5B52]/8 bg-white p-3 shadow-sm lg:flex-row lg:items-center">
        <PageSearchInput className="flex-1" testId="support-search" value={search} onSearch={setSearch} placeholder="Search case, reference or shop…" inputClassName="rounded-lg border-[#0E5B52]/10" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="ticket-status-filter" className="h-10 w-full rounded-lg lg:w-48"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid="ticket-priority-filter" className="h-10 w-full rounded-lg lg:w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All priorities</SelectItem>{PRIORITIES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
          <RefreshCw className={`h-4 w-4 ${listQuery.isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid min-h-[660px] overflow-hidden rounded-2xl border border-[#0E5B52]/10 bg-white shadow-[0_22px_60px_-40px_rgba(6,78,59,.4)] lg:grid-cols-[360px_minmax(0,1fr)] xl:grid-cols-[190px_380px_minmax(0,1fr)]">
        <aside className="hidden border-r border-[#0E5B52]/8 bg-[#F7FAF8] p-3 xl:block">
          <div className="px-2 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6B7280]">Saved queues</div>
          <div className="space-y-1">
            {QUEUES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setQueue(id)}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-xs font-medium transition ${
                  queue === id ? "bg-[#0E5B52] text-white shadow-sm" : "text-[#425466] hover:bg-[#0E5B52]/6"
                }`}
              >
                <Icon className="h-4 w-4" /><span className="flex-1">{label}</span>
                {id === "unassigned" && metrics.unassigned > 0 && <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${queue === id ? "bg-white/15" : "bg-amber-100 text-amber-800"}`}>{metrics.unassigned}</span>}
              </button>
            ))}
          </div>
          <div className="mt-6 rounded-xl border border-[#0E5B52]/8 bg-white p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#064E3B]"><ShieldCheck className="h-4 w-4" /> Queue discipline</div>
            <p className="mt-2 text-[11px] leading-5 text-[#6B7280]">Claim before replying. Use internal notes for investigation context.</p>
          </div>
        </aside>

        <section className="border-r border-[#0E5B52]/8">
          <div className="flex items-center justify-between border-b border-[#0E5B52]/8 px-4 py-3">
            <div>
              <div className="text-xs font-semibold text-[#064E3B]">{QUEUES.find((item) => item.id === queue)?.label}</div>
              <div className="text-[10px] text-[#6B7280]">{items.length} visible cases</div>
            </div>
            {listQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin text-[#0E5B52]" />}
          </div>
          <div className="max-h-[620px] overflow-y-auto">
            {listQuery.isLoading ? <CaseListSkeleton /> : listQuery.isError ? (
              <div className="p-6 text-center"><div className="text-sm font-medium">Queue unavailable</div><Button size="sm" variant="outline" className="mt-3" onClick={() => listQuery.refetch()}>Retry</Button></div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#6B7280]"><CheckCircle2 className="mx-auto mb-3 h-7 w-7 text-emerald-500" />This queue is clear.</div>
            ) : items.map((ticket) => (
              <CaseRow key={ticket.id} ticket={ticket} active={selectedId === ticket.id} onClick={() => setSelectedId(ticket.id)} />
            ))}
          </div>
        </section>

        <section className="hidden min-w-0 lg:block">
          {!selectedId ? <CasePlaceholder /> : detailQuery.isLoading ? <CaseDetailSkeleton /> : detailQuery.isError ? (
            <div className="flex h-full items-center justify-center p-8 text-center"><div><div className="font-semibold">Case could not load</div><Button variant="outline" size="sm" className="mt-3" onClick={() => detailQuery.refetch()}>Try again</Button></div></div>
          ) : selected ? (
            <CaseRoom
              key={selected.id}
              currentAdmin={currentAdmin}
              ticket={selected}
              queryClient={queryClient}
            />
          ) : <CasePlaceholder />}
        </section>
      </div>

      {selected && (
        <div className="mt-4 lg:hidden">
          <CaseRoom key={selected.id} currentAdmin={currentAdmin} ticket={selected} queryClient={queryClient} compact />
        </div>
      )}
    </div>
  );
}

function CaseRow({ active, onClick, ticket }) {
  const timing = sla(ticket);
  return (
    <button
      data-testid={`ticket-row-${ticket.id}`}
      onClick={onClick}
      className={`group w-full border-b border-[#0E5B52]/7 px-4 py-4 text-left transition ${
        active ? "bg-[#0E5B52]/[0.065] shadow-[inset_3px_0_0_#0E5B52]" : "hover:bg-[#F7FAF8]"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
          ticket.priority === "urgent" ? "bg-rose-500 ring-4 ring-rose-100" : ticket.priority === "high" ? "bg-orange-400" : "bg-sky-400"
        }`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">{ticket.publicReference}</span>
            <span className="text-[10px] text-[#9CA3AF]">{timeAgo(ticket.updatedAt)}</span>
          </div>
          <div className="mt-1 truncate text-sm font-semibold text-[#064E3B]">{ticket.subject}</div>
          <div className="mt-1 truncate text-[11px] text-[#6B7280]">{ticket.shopName} · {ticket.category.name}</div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold ${statusTone(ticket.status)}`}>{titleCase(ticket.status)}</span>
            <span className={`rounded-md px-2 py-1 text-[9px] font-semibold ${timing.tone}`}><Clock3 className="mr-1 inline h-3 w-3" />{timing.label}</span>
          </div>
        </div>
        <ChevronRight className={`mt-5 h-4 w-4 shrink-0 transition ${active ? "text-[#0E5B52]" : "text-[#C3CEC9] group-hover:translate-x-0.5"}`} />
      </div>
    </button>
  );
}

function CaseRoom({ compact = false, currentAdmin, queryClient, ticket }) {
  const navigate = useNavigate();
  const [reply, setReply] = useState("");
  const [visibility, setVisibility] = useState("public");
  const updateMutation = useMutation({
    mutationFn: (patch) => updateAdminSupportTicket(ticket.id, { ...patch, version: ticket.version }),
    onSuccess: async (payload) => {
      queryClient.setQueryData(["admin-support-detail", ticket.id], payload);
      await queryClient.invalidateQueries({ queryKey: ["admin-support"] });
      toast.success("Case updated");
    },
    onError: (error) => {
      toast.error(error.message || "Case could not be updated");
      queryClient.invalidateQueries({ queryKey: ["admin-support-detail", ticket.id] });
    },
  });
  const replyMutation = useMutation({
    mutationFn: () => replyAdminSupportTicket(ticket.id, reply.trim(), visibility),
    onSuccess: async (payload) => {
      setReply("");
      queryClient.setQueryData(["admin-support-detail", ticket.id], payload);
      await queryClient.invalidateQueries({ queryKey: ["admin-support"] });
      toast.success(visibility === "internal" ? "Internal note saved" : "Reply sent to merchant");
    },
    onError: (error) => toast.error(error.message || "Message could not be sent"),
  });
  const timing = sla(ticket);
  const isClosed = ticket.status === "closed";
  const claimedByMe = ticket.assigneeId === currentAdmin?.id;
  const contextEntries = Object.entries(ticket.context || {}).filter(([, value]) => value !== "" && value != null).slice(0, 8);

  return (
    <div className={`flex min-h-[660px] flex-col bg-white ${compact ? "overflow-hidden rounded-2xl border" : ""}`}>
      <header className="border-b border-[#0E5B52]/8 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={`rounded-full text-[9px] ${statusTone(ticket.status)}`}>{titleCase(ticket.status)}</Badge>
              <Badge variant="outline" className={`rounded-full text-[9px] ${priorityTone(ticket.priority)}`}>{titleCase(ticket.priority)}</Badge>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">{ticket.publicReference}</span>
            </div>
            <h2 className="mt-2 truncate text-lg font-semibold tracking-[-0.02em] text-[#064E3B]">{ticket.subject}</h2>
            <div className="mt-1 text-xs text-[#6B7280]">{ticket.shopName} · {ticket.branchName || "Shop-wide"} · {ticket.requesterName}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!ticket.assigneeId && !isClosed ? (
            <Button
              size="sm"
              onClick={() => updateMutation.mutate({ assigneeId: currentAdmin?.id, status: ticket.status === "new" ? "open" : ticket.status })}
              disabled={updateMutation.isPending}
              className="h-8 rounded-lg bg-[#0E5B52]"
            >
              <UserCheck className="mr-1.5 h-3.5 w-3.5" /> Claim case
            </Button>
          ) : (
            <span className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-medium ${claimedByMe ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
              <CircleDot className="h-3 w-3" /> {claimedByMe ? "Assigned to you" : ticket.assigneeName}
            </span>
          )}
          <Select disabled={isClosed} value={ticket.status} onValueChange={(value) => updateMutation.mutate({ status: value })}>
            <SelectTrigger data-testid="ticket-status-update" className="h-8 w-44 rounded-lg text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
          <Select disabled={isClosed} value={ticket.priority} onValueChange={(value) => updateMutation.mutate({ priority: value })}>
            <SelectTrigger data-testid="ticket-priority-update" className="h-8 w-32 rounded-lg text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
          <span className={`ml-auto rounded-lg px-2.5 py-1.5 text-[10px] font-semibold ${timing.tone}`}><Clock3 className="mr-1 inline h-3 w-3" />{timing.label}</span>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 2xl:grid-cols-[minmax(0,1fr)_250px]">
        <div className="flex min-h-0 flex-col">
          <div className="min-h-[300px] flex-1 space-y-4 overflow-y-auto bg-[radial-gradient(circle_at_top,#F0F6F1,transparent_48%)] px-5 py-5">
            {(ticket.messages || []).map((message) => {
              const adminMessage = message.authorType === "admin";
              const internal = message.visibility === "internal";
              return (
                <div key={message.id} className={`flex ${adminMessage && !internal ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[86%] rounded-2xl px-4 py-3 ${
                    internal
                      ? "w-full border border-amber-200 bg-amber-50 text-amber-950"
                      : adminMessage
                        ? "rounded-br-md bg-[#0E5B52] text-white"
                        : "rounded-bl-md border border-[#0E5B52]/8 bg-white text-[#18342f] shadow-sm"
                  }`}>
                    <div className={`text-[9px] font-semibold uppercase tracking-wider ${
                      internal ? "text-amber-700" : adminMessage ? "text-white/60" : "text-[#6B7280]"
                    }`}>
                      {internal ? "Internal note" : message.authorName} · {timeAgo(message.createdAt)}
                    </div>
                    <div className="mt-1.5 whitespace-pre-wrap text-sm leading-6">{message.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {isClosed ? (
            <div className="border-t border-[#0E5B52]/8 bg-[#F7FAF8] p-4">
              <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                  <LockKeyhole className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#064E3B]">Case record sealed</div>
                  <div className="mt-0.5 text-[10px] leading-4 text-[#6B7280]">Closed cases are read-only to preserve the support audit trail. Create a linked case if the issue returns.</div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`border-t p-4 ${visibility === "internal" ? "border-amber-200 bg-amber-50/55" : "border-[#0E5B52]/8 bg-white"}`}>
              <div className="mb-2 flex items-center justify-between">
                <div className="inline-flex rounded-lg border bg-white p-0.5">
                  <button onClick={() => setVisibility("public")} className={`rounded-md px-3 py-1.5 text-[10px] font-semibold ${visibility === "public" ? "bg-[#0E5B52] text-white" : "text-[#6B7280]"}`}>Public reply</button>
                  <button onClick={() => setVisibility("internal")} className={`rounded-md px-3 py-1.5 text-[10px] font-semibold ${visibility === "internal" ? "bg-amber-500 text-white" : "text-[#6B7280]"}`}>Internal note</button>
                </div>
                <span className="text-[9px] text-[#9CA3AF]">{visibility === "internal" ? "Only admins can see this" : "Visible to the merchant"}</span>
              </div>
              <div className="flex items-end gap-2">
                <Textarea
                  data-testid="ticket-reply-input"
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  rows={2}
                  className="min-h-[52px] resize-none rounded-xl bg-white"
                  placeholder={visibility === "internal" ? "Add investigation context…" : "Write a clear, helpful reply…"}
                />
                <Button
                  data-testid="send-reply-btn"
                  size="icon"
                  disabled={!reply.trim() || replyMutation.isPending}
                  onClick={() => replyMutation.mutate()}
                  className={`h-[52px] w-[52px] shrink-0 rounded-xl ${visibility === "internal" ? "bg-amber-500 hover:bg-amber-600" : "bg-[#0E5B52] hover:bg-[#0B4A43]"}`}
                >
                  {replyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
        </div>

        <aside className="hidden border-l border-[#0E5B52]/8 bg-[#FBFCFB] p-4 2xl:block">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">Merchant context</div>
          <div className="mt-3 rounded-xl border border-[#0E5B52]/8 bg-white p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0E5B52]/8 text-[#0E5B52]"><MessageSquareText className="h-4 w-4" /></div>
            <div className="mt-3 text-sm font-semibold text-[#064E3B]">{ticket.shopName}</div>
            <div className="mt-0.5 text-[11px] text-[#6B7280]">{ticket.requesterEmail || ticket.requesterName}</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/shops/${ticket.shopId}`)}
              className="mt-2 h-7 px-0 text-[10px] text-[#0E5B52]"
            >
              Open shop profile <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <div className="mt-4 space-y-2">
            {contextEntries.length ? contextEntries.map(([label, value]) => (
              <div key={label} className="rounded-lg bg-white px-3 py-2 ring-1 ring-[#0E5B52]/7">
                <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">{titleCase(label)}</div>
                <div className="mt-0.5 truncate text-[11px] font-medium text-[#33443f]">{String(value)}</div>
              </div>
            )) : <div className="rounded-lg bg-white p-3 text-[10px] leading-5 text-[#6B7280] ring-1 ring-[#0E5B52]/7">No diagnostic context was shared.</div>}
          </div>
          <div className="mt-4 text-[10px] leading-5 text-[#9CA3AF]">
            Created {fmtDateTime(ticket.createdAt)}<br />Last activity {timeAgo(ticket.updatedAt)}
          </div>
        </aside>
      </div>
    </div>
  );
}

function CaseListSkeleton() {
  return <div>{Array.from({ length: 7 }, (_, index) => <div key={index} className="space-y-2 border-b p-4"><Skeleton className="h-3 w-24" /><Skeleton className="h-4 w-4/5" /><Skeleton className="h-3 w-3/5" /><Skeleton className="mt-3 h-6 w-full" /></div>)}</div>;
}

function CaseDetailSkeleton() {
  return <div className="space-y-4 p-6"><Skeleton className="h-5 w-40" /><Skeleton className="h-7 w-4/5" /><Skeleton className="h-10 w-full" /><div className="pt-8"><Skeleton className="h-24 w-3/4 rounded-2xl" /><Skeleton className="ml-auto mt-4 h-20 w-2/3 rounded-2xl" /></div></div>;
}

function CasePlaceholder() {
  return (
    <div className="flex h-full min-h-[620px] items-center justify-center bg-[radial-gradient(circle_at_center,#F0F6F1,transparent_55%)] p-8 text-center">
      <div>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0E5B52]/8 text-[#0E5B52]"><LifeBuoy className="h-6 w-6" /></div>
        <div className="mt-4 font-semibold text-[#064E3B]">Select a case to enter the room</div>
        <div className="mt-1 text-xs text-[#6B7280]">Conversation, context and controls will appear here.</div>
      </div>
    </div>
  );
}
